import React, { useState, useRef } from "react";
import { IconButton, Tooltip, Box, TextField } from "@mui/material";
import { ArrowDropDown, Close } from "@mui/icons-material";
import SearchableSelectModal, {
  SelectableOption,
} from "../searchable-select-modal";
import { deepPurple, grey } from "@mui/material/colors";

const sxStyles = {
  textField: {
    cursor: "pointer",
    [`& .MuiInputBase-input`]: {
      cursor: "pointer",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: grey[800] + " !important",
      caretColor: grey[800],
      "::placeholder": {
        color: grey[600] + " !important",
        opacity: 1,
      },
    },
    [`& .MuiInputLabel-root`]: {
      pointerEvents: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled)::before": {
      borderBottomColor: deepPurple[800],
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: deepPurple[800],
    },
    "&.Mui-focused .MuiInputBase-input": {
      color: grey[800] + " !important",
    },
    "&.Mui-focused .MuiInputBase-input::placeholder": {
      color: grey[600] + " !important",
      opacity: 1,
    },
  },
};

interface FilterOption extends SelectableOption {
  id: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selectedValues?: string[];
  onChange: (values: string[]) => void;
  onReset: () => void;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues = [],
  onChange,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Convert string options to SelectableOptions
  const selectableOptions: FilterOption[] = options.map((option) => ({
    id: option,
    label: option,
  }));

  const selectedOptions: FilterOption[] = selectedValues.map((value) => ({
    id: value,
    label: value,
  }));

  const displayText =
    selectedValues.length > 0 ? selectedValues.join(", ") : "";
  const isLongText = displayText.length > 16;
  const selectedDisplayText = isLongText
    ? `${selectedValues.length} selected`
    : displayText;

  const handleMouseEnter = () => {
    if (isLongText) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleSave = (selections: FilterOption[]) => {
    const values = selections.map((s) => s.id);
    onChange(values);
    setIsOpen(false);
    requestAnimationFrame(() => {
      inputRef.current?.blur();
    });
  };

  const handleCancel = () => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      inputRef.current?.blur();
    });
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReset();
  };

  const handleFieldClick = () => {
    setIsOpen(true);
  };

  return (
    <>
      <Tooltip open={showTooltip} title={displayText} arrow placement="top">
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ display: "flex", width: "100%", alignItems: "center" }}
        >
          <TextField
            ref={fieldRef}
            inputRef={inputRef}
            label={label}
            value={selectedDisplayText}
            placeholder={
              selectedDisplayText ? "" : `Select ${label.toLowerCase()}`
            }
            variant="standard"
            fullWidth
            onClick={handleFieldClick}
            slotProps={{
              inputLabel: {
                shrink: selectedDisplayText.length > 0,
                sx: {
                  color: grey[600] + " !important",
                  "&.Mui-focused": {
                    color: grey[800] + " !important",
                  },
                },
              },
              input: {
                readOnly: true,
                endAdornment:
                  selectedValues.length > 0 ? (
                    <IconButton
                      size="small"
                      onClick={handleReset}
                      sx={{
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : (
                    <ArrowDropDown
                      sx={{
                        color: grey[600],
                        pointerEvents: "none",
                      }}
                    />
                  ),
              },
            }}
            sx={sxStyles.textField}
          />
        </Box>
      </Tooltip>

      <SearchableSelectModal
        open={isOpen}
        anchorElement={fieldRef.current}
        options={selectableOptions}
        selectedValues={selectedOptions}
        onSave={handleSave}
        onCancel={handleCancel}
        onClose={handleCancel}
        placeholder={`Search for ${label.toLowerCase()}`}
        multiple={true}
        width="23rem"
      />
    </>
  );
};

export default MultiSelectFilter;
